import styles from "../../styles/CoordinationBoards.module.css"
import { useContext, useState, useEffect, useRef } from "react"
import { useQuery } from "@apollo/client"
import { useParams } from "react-router-dom"
import { UserContext } from "../../context/userContext"
import axios from "axios"
import { coordinationBoardsApiUrl } from "../../config/microservicesUrls"
import { LoaderMedium } from "../../components/Loaders"
import { Toast } from "primereact/toast"
import { BoardType } from "./Types/Enums"
import { GET_ORGANIZATION, showError } from "./Utils/Utils"
import { ReactComponent as OrgBoardIcon } from "./resources/org-board.svg"
import { ReactComponent as EnterpriseIcon } from "./resources/enterprise-board.svg"
import { Tooltip } from 'primereact/tooltip'
import Board from "./Board"

const api_url = coordinationBoardsApiUrl()

const CoordinationBoards = () => {
    const { idBoard, idTopic } = useParams()
    const userCtx = useContext(UserContext)
    const [loading, setLoading] = useState(true)
    const [boards, setBoards] = useState([])
    const [organizationsDic, setOrganizationsDic] = useState({})
    const [selectedBoard, setSelectedBoard] = useState(null)
    const [defaultTags, setDefaultTags] = useState([])
    const toast = useRef(null)

    const { refetch } = useQuery(GET_ORGANIZATION, {
        variables: {
            organization_id: userCtx.session.user.organization_id,
            staff_id: userCtx.session.user.staff_id
        },
        skip: true,
    })

    const getOrganizationName = async (organizationId) => {
        if (organizationId in organizationsDic) {
            return organizationsDic[organizationId]
        } else {
            const res = await refetch({
                organization_id: organizationId,
                staff_id: userCtx.session.user.staff_id
            })
            if (res.data.organization) {
                setOrganizationsDic({ ...organizationsDic, organizationId: res.data.organization.name })
                return res.data.organization.name
            }
            return organizationId
        }
    }

    useEffect(() => {
        setLoading(true)

        const config = {
            headers: {
                Authorization: `Bearer ${userCtx.tokenCoordinationBoards}`,
            }
        }

        const fetchBoards = axios.post(`${api_url}/boards/staffBoards`, {
            enterpriseId: userCtx.session.user.enterprise_id,
            organizationIds: [userCtx.session.user.organization_id],
            isPartner: userCtx.session.user.organization_type === "PARTNER" && [9, 10].includes(userCtx.session.user.staff_role),
            getAll: false
        }, config)

        const fetchTags = axios.get(`${api_url}/tags`, config)

        const promises = [fetchBoards]
        setSelectedBoard(null)


        if (defaultTags.length === 0) {
            promises.push(fetchTags)
        }

        Promise.all(promises).then(async (results) => {
            const [boardsResponse, tagsResponse] = results
            let _boards = []
            if (boardsResponse.status === 200) {
                _boards = await boardsResponse.data
                _boards = await Promise.all(_boards.map(async (board) => {
                    let name = await getOrganizationName(board.ownerEnterpriseId)
                    if (board.type === BoardType.Default) {
                        name = await getOrganizationName(board.ownerOrganizationId)
                    }
                    if (board.type === BoardType["Referral Partner"]) {
                        name = `${await getOrganizationName(board.ownerOrganizationId)} : ${await getOrganizationName(board.organizations[0])}`
                    }
                    if (board.type === BoardType["Cross Organization"]) {
                        name = `${await getOrganizationName(board.organizations[0])} : ${await getOrganizationName(board.organizations[1])}`
                    }
                    board.name = name
                    return board
                }))
                setBoards(_boards)
            }
            if (tagsResponse && tagsResponse.status === 200) {
                setDefaultTags(tagsResponse.data)
            }
            if (idBoard) {
                const board = _boards.find(b => b.id === idBoard)
                if (board) {
                    setSelectedBoard(board)
                }
            }
            setLoading(false)
        }).catch((error) => {
            setLoading(false)
            const errors = error.response && error.response.data.errors
            showError(toast, "Not able to get data", errors)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCtx.session.user.organization_id])

    if (loading) {
        return <LoaderMedium></LoaderMedium>
    }

    if (selectedBoard != null) {
        return (
            <>
                <Toast ref={toast} />
                <Board
                    board={selectedBoard}
                    closeView={() => setSelectedBoard(null)}
                    defaultTags={defaultTags}
                    selectedTopicId={idTopic}
                />
            </>
        )
    }
    return (
        <>
            <Toast ref={toast} />

            <div className={styles.coordination_board}>
                <h1>Coordination Board</h1>
                <h2>Select the board you want to view</h2>
                <hr />
                <Tooltip target=".board-tooltip" className="custom-tooltip" />
                <div className={styles.boards}>
                    {boards.filter((b) => b.type === BoardType.Enterprise).map((board, index) => {
                        return (
                            <button
                                key={`ent_${index}`}
                                className={`${styles.board_card} ${board.readOnly ? "board-tooltip" : ''}`}
                                onClick={() => setSelectedBoard(board)}
                                data-pr-tooltip="Read-only"
                                data-pr-position="bottom"
                                data-pr-at="center bottom"
                            >
                                <EnterpriseIcon />
                                <h3>{board.name}</h3>
                            </button>
                        )
                    })}
                    {boards.filter((b) => b.type === BoardType.Default).map((board, index) => {
                        return (
                            <button
                                key={`def_${index}`}
                                className={`${styles.board_card} ${board.readOnly ? "board-tooltip" : ''}`}
                                data-pr-tooltip="Read-only"
                                data-pr-position="bottom"
                                data-pr-at="center bottom"
                                onClick={() => setSelectedBoard(board)}
                            >
                                <OrgBoardIcon />
                                <h3>{board.name}</h3>
                            </button>
                        )
                    })}
                </div>
                {boards.filter((b) => b.type === BoardType["Referral Partner"]).length > 0 &&
                    <div className={styles.referral_partner_boards}>
                        <h2>Referral Partner Boards</h2>
                        <div className={styles.boards}>
                            {boards.filter((b) => b.type === BoardType["Referral Partner"]).map((board, index) => {
                                return (
                                    <button
                                        key={`referral_${index}`}
                                        className={`${styles.board_card} ${board.readOnly ? "board-tooltip" : ''}`}
                                        data-pr-tooltip="Read-only"
                                        data-pr-position="bottom"
                                        data-pr-at="center bottom"
                                        onClick={() => setSelectedBoard(board)}
                                    >
                                        <h3>Referral Partner</h3>
                                        <h4>{board.name}</h4>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                }
                {boards.filter((b) => b.type === BoardType["Cross Organization"]).length > 0 &&
                    <div className={styles.cross_org_boards}>
                        <h2>Cross-Organization Boards</h2>
                        <div className={styles.boards}>
                            {boards.filter((b) => b.type === BoardType["Cross Organization"]).map((board, index) => {
                                return (
                                    <button
                                        key={`cross_${index}`}
                                        className={`${styles.board_card} ${board.readOnly ? "board-tooltip" : ''}`}
                                        data-pr-tooltip="Read-only"
                                        data-pr-position="bottom"
                                        data-pr-at="center bottom"
                                        onClick={() => setSelectedBoard(board)}
                                    >
                                        <h3>Cross-Organization</h3>
                                        <h4>{board.name}</h4>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                }

            </div>
        </>
    )
}

export default CoordinationBoards